import {
  TokenAuthRequestPayload,
  TokenAuthResponsePayload,
} from '@quality24/feedback-typings';
import { UserSession } from './UserSession';

export interface ReactAuthConfig {
  appName: string;
  endpoint: string;
  headers?: Record<string, string>;
  userPoolClientId: string;
}

export class Authenticator {
  private static config: ReactAuthConfig;

  private static session: UserSession;

  /**
   * Initializes the configuration
   * @param config
   */
  static configure(config: ReactAuthConfig) {
    this.config = config;

    // Read storage to build credentials
    this.session = new UserSession(config.appName, config.userPoolClientId);
  }

  /**
   * Returns the clientId value
   * @returns
   */
  static getClientId() {
    return this.config.userPoolClientId;
  }

  /**
   * Returns whether cookies contain session
   * @returns
   */
  static getSession() {
    return this.session;
  }

  /**
   * Returns current session data
   * @returns
   */
  static async currentSession() {
    if (!this.session.getIdToken()) {
      return Promise.reject(new Error('User not logged in'));
    }
    if (!this.session.isValid()) {
      return Promise.reject(new Error('Session expired'));
    }
    return this.session;
  }

  /**
   * Signs user in
   * @param username
   * @param password
   * @returns
   */
  static async signIn(token: string) {
    // Perform POST to API
    const result = await fetch(`${this.config.endpoint}/v1/login/token`, {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token } as TokenAuthRequestPayload),
    });

    // Check if result was successful (not 4xx)
    if (!result.ok) {
      throw new Error(`Failed to sign in via auth API: ${result.statusText}`);
    }

    // Save session with result
    const responseBody = await result.json();
    this.session.initSession(responseBody);
    return {
      ...responseBody,
      userData: {
        ...responseBody.userData,
        ...this.session.getUserData(),
      },
    } as TokenAuthResponsePayload;
  }

  /**
   * Signs user out
   * @param username
   * @param password
   * @returns
   */
  static async signOut() {
    this.session.purge();
  }
}
